/**
 * EsocialCadastroSection — UI para envio dos eventos de cadastro inicial do eSocial:
 *   • S-1000 Informações do Empregador (obrigatório antes de qualquer outro)
 *   • S-1005 Tabela de Estabelecimentos
 *   • S-1010 Tabela de Rubricas
 *
 * Cada bloco abre um dialog com os campos mínimos, gera o evento e
 * delega assinar/transmitir/consultar para os endpoints existentes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Building2, FileText, BookOpenCheck, MapPin, AlertTriangle, UserPlus,
  Loader2, Send, FileSignature, CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import MayaRejectionAnalysisDialog from './MayaRejectionAnalysisDialog';
import MayaLintDialog from './MayaLintDialog';
import MayaAvatar from '../maya/MayaAvatar';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const StatusBadge = ({ status }) => {
  const map = {
    rascunho: { color: 'bg-slate-400', icon: FileSignature, label: 'Rascunho' },
    assinado: { color: 'bg-blue-500', icon: FileSignature, label: 'Assinado' },
    transmitido: { color: 'bg-amber-500', icon: Clock, label: 'Transmitido' },
    processado_ok: { color: 'bg-emerald-600', icon: CheckCircle2, label: 'Processado OK' },
    rejeitado: { color: 'bg-red-600', icon: XCircle, label: 'Rejeitado' },
    cancelado: { color: 'bg-slate-300', icon: XCircle, label: 'Cancelado' },
  };
  const m = map[status] || map.rascunho;
  const Icon = m.icon;
  return (
    <Badge className={`${m.color} text-white text-[10px] gap-1`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
};

const flow = async (eventoId, setBusy, reload) => {
  setBusy(eventoId);
  try {
    await axios.post(`${API}/api/esocial/eventos/${eventoId}/assinar`, {}, auth());
    toast.info('Assinatura OK. Transmitindo…');
    await axios.post(`${API}/api/esocial/eventos/${eventoId}/transmitir`, {}, auth());
    toast.info('Lote enviado ao gov.br. Aguarde…');
    await new Promise((r) => setTimeout(r, 8000));
    const c = await axios.post(`${API}/api/esocial/eventos/${eventoId}/consultar`, {}, auth());
    const cd = c.data?.codigo_resposta;
    if (cd && cd.startsWith('2')) {
      toast.success(`✅ Aceito pelo gov.br — recibo gerado`);
    } else {
      toast.error(`❌ gov.br rejeitou (cd=${cd}): ${c.data?.descricao_resposta || 'ver detalhes'}`);
    }
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Erro no fluxo eSocial');
  } finally {
    setBusy(null);
    reload();
  }
};

// ---------------------------------------------------------------------------
// Dialog S-1000
// ---------------------------------------------------------------------------
const S1000Dialog = ({ open, onOpenChange, companyId, onCreated }) => {
  const [form, setForm] = useState({
    classe_tributaria: '01',
    ini_validade: new Date().toISOString().slice(0, 7),
    contato_nome: '',
    contato_cpf: '',
    contato_fone: '',
    contato_email: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/s1000/gerar?company_id=${companyId}`,
        { ...form, ambiente: '2' },
        auth(),
      );
      toast.success('S-1000 gerado em rascunho');
      onCreated(r.data.evento_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar S-1000');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>S-1000 — Informações do Empregador</DialogTitle>
          <DialogDescription>
            Evento de cadastro inicial. <b>Obrigatório</b> antes de qualquer outro evento.
            Use ambiente 2 (Produção Restrita / Homologação) para testes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Classificação tributária *</Label>
            <Select value={form.classe_tributaria} onValueChange={(v) => setForm({ ...form, classe_tributaria: v })}>
              <SelectTrigger data-testid="s1000-classtrib"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="01">01 — Simples Nacional</SelectItem>
                <SelectItem value="02">02 — Empresa lucro presumido (Anexo IV)</SelectItem>
                <SelectItem value="03">03 — Empresa lucro real/arbitrado</SelectItem>
                <SelectItem value="06">06 — MEI</SelectItem>
                <SelectItem value="07">07 — Entes públicos (RPPS)</SelectItem>
                <SelectItem value="99">99 — Demais</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Início da validade (AAAA-MM) *</Label>
            <Input value={form.ini_validade} onChange={(e) => setForm({ ...form, ini_validade: e.target.value })}
                   placeholder="2026-01" data-testid="s1000-inivalid" />
          </div>
          <div className="text-xs text-slate-500 italic">
            Os dados de razão social e contato são puxados automaticamente pelo gov.br a partir do CNPJ na Receita Federal.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="s1000-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : 'Gerar rascunho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Dialog S-1005
// ---------------------------------------------------------------------------
const S1005Dialog = ({ open, onOpenChange, companyId, onCreated }) => {
  const [form, setForm] = useState({
    cnae_preponderante: '',
    ini_validade: new Date().toISOString().slice(0, 7),
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/s1005/gerar?company_id=${companyId}`,
        { ...form, ambiente: '2' }, auth(),
      );
      toast.success('S-1005 gerado em rascunho');
      onCreated(r.data.evento_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar S-1005');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>S-1005 — Tabela de Estabelecimentos</DialogTitle>
          <DialogDescription>
            Define o CNAE preponderante. RAT/FAP são puxados automaticamente do
            cadastro da Receita — só preencher se houver processo administrativo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>CNAE Preponderante (7 dígitos) *</Label>
            <Input value={form.cnae_preponderante}
                   onChange={(e) => setForm({ ...form, cnae_preponderante: e.target.value.replace(/\D/g, '') })}
                   placeholder="5229099" maxLength={7} data-testid="s1005-cnae" />
          </div>
          <div>
            <Label>Início da validade (AAAA-MM) *</Label>
            <Input value={form.ini_validade}
                   onChange={(e) => setForm({ ...form, ini_validade: e.target.value })}
                   placeholder="2026-01" data-testid="s1005-inivalid" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !form.cnae_preponderante} data-testid="s1005-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : 'Gerar rascunho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Dialog S-1010
// ---------------------------------------------------------------------------
const S1010Dialog = ({ open, onOpenChange, companyId, onCreated }) => {
  const [form, setForm] = useState({
    cod_rubrica: '1000',
    ide_tab_rubrica: '1',
    descricao_rubrica: 'SALARIO BASE',
    natureza_rubrica: '1000',
    tipo_rubrica: '1',
    ini_validade: new Date().toISOString().slice(0, 7),
    cod_inc_cp: '11',
    cod_inc_irrf: '11',
    cod_inc_fgts: '11',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/s1010/gerar?company_id=${companyId}`,
        { ...form, ambiente: '2' }, auth(),
      );
      toast.success('S-1010 gerado em rascunho');
      onCreated(r.data.evento_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar S-1010');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>S-1010 — Tabela de Rubricas</DialogTitle>
          <DialogDescription>
            Cadastra uma rubrica (ex: Salário Base). Repita para cada rubrica da sua folha.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Código</Label>
              <Input value={form.cod_rubrica} onChange={(e) => setForm({ ...form, cod_rubrica: e.target.value })}
                     data-testid="s1010-codrubr" />
            </div>
            <div>
              <Label>ID Tabela</Label>
              <Input value={form.ide_tab_rubrica} onChange={(e) => setForm({ ...form, ide_tab_rubrica: e.target.value })} />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo_rubrica} onValueChange={(v) => setForm({ ...form, tipo_rubrica: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Vencimento</SelectItem>
                  <SelectItem value="2">2 - Desconto</SelectItem>
                  <SelectItem value="3">3 - Informativa</SelectItem>
                  <SelectItem value="4">4 - Informat. Dedutora</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Descrição</Label>
            <Input value={form.descricao_rubrica} onChange={(e) => setForm({ ...form, descricao_rubrica: e.target.value })}
                   data-testid="s1010-dscrubr" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Natureza (4 dígitos)</Label>
              <Input value={form.natureza_rubrica} onChange={(e) => setForm({ ...form, natureza_rubrica: e.target.value })}
                     maxLength={4} />
            </div>
            <div>
              <Label>Início validade</Label>
              <Input value={form.ini_validade} onChange={(e) => setForm({ ...form, ini_validade: e.target.value })}
                     placeholder="2026-01" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Inc. CP</Label>
              <Input value={form.cod_inc_cp} onChange={(e) => setForm({ ...form, cod_inc_cp: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Inc. IRRF</Label>
              <Input value={form.cod_inc_irrf} onChange={(e) => setForm({ ...form, cod_inc_irrf: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Inc. FGTS</Label>
              <Input value={form.cod_inc_fgts} onChange={(e) => setForm({ ...form, cod_inc_fgts: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="s1010-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : 'Gerar rascunho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Dialog S-1020 (Lotação)
// ---------------------------------------------------------------------------
const S1020Dialog = ({ open, onOpenChange, companyId, onCreated }) => {
  const [form, setForm] = useState({
    cod_lotacao: 'MATRIZ',
    tp_lotacao: '01',
    fpas: '515',
    cod_tercs: '0000',
    ini_validade: new Date().toISOString().slice(0, 7),
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/esocial/s1020/gerar?company_id=${companyId}`,
        { ...form, ambiente: '2' }, auth(),
      );
      toast.success('S-1020 gerado em rascunho');
      onCreated(r.data.evento_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar S-1020');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>S-1020 — Tabela de Lotações Tributárias</DialogTitle>
          <DialogDescription>
            Define a lotação (matriz/filial) com FPAS e código de terceiros.
            Para Simples Nacional use codTercs=0000.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Código *</Label>
              <Input value={form.cod_lotacao}
                     onChange={(e) => setForm({ ...form, cod_lotacao: e.target.value })}
                     data-testid="s1020-codlot" />
            </div>
            <div>
              <Label>Tipo (Tabela 10)</Label>
              <Select value={form.tp_lotacao} onValueChange={(v) => setForm({ ...form, tp_lotacao: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="01">01 — Matriz/Filial</SelectItem>
                  <SelectItem value="02">02 — Obra de construção civil</SelectItem>
                  <SelectItem value="03">03 — Trabalhador avulso (OGMO)</SelectItem>
                  <SelectItem value="04">04 — Cooperativas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>FPAS *</Label>
              <Input value={form.fpas}
                     onChange={(e) => setForm({ ...form, fpas: e.target.value.replace(/\D/g, '') })}
                     placeholder="515" maxLength={4} />
            </div>
            <div>
              <Label>Cód. Terceiros</Label>
              <Input value={form.cod_tercs}
                     onChange={(e) => setForm({ ...form, cod_tercs: e.target.value.replace(/\D/g, '') })}
                     placeholder="0000" maxLength={4} />
            </div>
          </div>
          <div>
            <Label>Início da validade (AAAA-MM)</Label>
            <Input value={form.ini_validade}
                   onChange={(e) => setForm({ ...form, ini_validade: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="s1020-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : 'Gerar rascunho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Dialog S-1070 (Processos Administrativos / Judiciais)
// ---------------------------------------------------------------------------
const S1070Dialog = ({ open, onOpenChange, companyId, onCreated }) => {
  const [form, setForm] = useState({
    tp_proc: '1',
    nr_proc: '',
    ind_mat_proc: '1',
    ini_validade: new Date().toISOString().slice(0, 7),
    observacao: '',
    cod_susp: '1',
    ind_susp: '03',
    dt_decisao: new Date().toISOString().slice(0, 10),
    ind_deposito: 'N',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        tp_proc: form.tp_proc,
        nr_proc: form.nr_proc.replace(/\D/g, ''),
        ind_mat_proc: form.ind_mat_proc,
        ini_validade: form.ini_validade,
        observacao: form.observacao || null,
        info_susp: form.ind_mat_proc === '1'
          ? [{ cod_susp: form.cod_susp, ind_susp: form.ind_susp,
               dt_decisao: form.dt_decisao, ind_deposito: form.ind_deposito }]
          : null,
        ambiente: '2',
      };
      const r = await axios.post(
        `${API}/api/esocial/s1070/gerar?company_id=${companyId}`, body, auth(),
      );
      toast.success('S-1070 gerado em rascunho');
      onCreated(r.data.evento_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar S-1070');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>S-1070 — Processos Administrativos/Judiciais</DialogTitle>
          <DialogDescription>
            Cadastra processo que suspende exigibilidade tributária ou FGTS. Necessário antes de
            enviar eventos remuneratórios afetados pelo processo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tp_proc} onValueChange={(v) => setForm({ ...form, tp_proc: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Administrativo</SelectItem>
                  <SelectItem value="2">2 — Judicial</SelectItem>
                  <SelectItem value="4">4 — Notificação Fiscal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Matéria</Label>
              <Select value={form.ind_mat_proc}
                      onValueChange={(v) => setForm({ ...form, ind_mat_proc: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Tributária / FGTS</SelectItem>
                  <SelectItem value="7">7 — FGTS LC 110/2001</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Número do processo *</Label>
            <Input value={form.nr_proc}
                   onChange={(e) => setForm({ ...form, nr_proc: e.target.value })}
                   placeholder="17/21 (adm) · 20 (jud) · 16 (notif)"
                   data-testid="s1070-nrproc" />
          </div>
          <div>
            <Label>Início validade (AAAA-MM)</Label>
            <Input value={form.ini_validade}
                   onChange={(e) => setForm({ ...form, ini_validade: e.target.value })} />
          </div>
          <div>
            <Label>Observação</Label>
            <Input value={form.observacao}
                   onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
          </div>
          {form.ind_mat_proc === '1' && (
            <div className="border rounded-md p-3 space-y-2 bg-slate-50">
              <div className="text-xs font-semibold text-slate-700">
                Informações da Suspensão (obrigatório para matéria tributária)
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Cód. Susp.</Label>
                  <Input value={form.cod_susp}
                         onChange={(e) => setForm({ ...form, cod_susp: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Ind. Susp.</Label>
                  <Select value={form.ind_susp}
                          onValueChange={(v) => setForm({ ...form, ind_susp: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="03">03 — Depósito administrativo</SelectItem>
                      <SelectItem value="14">14 — Contestação FAP</SelectItem>
                      <SelectItem value="92">92 — Decisão definitiva</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Ind. Depósito</Label>
                  <Select value={form.ind_deposito}
                          onValueChange={(v) => setForm({ ...form, ind_deposito: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="S">S — Sim</SelectItem>
                      <SelectItem value="N">N — Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Data da decisão</Label>
                <Input type="date" value={form.dt_decisao}
                       onChange={(e) => setForm({ ...form, dt_decisao: e.target.value })} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="s1070-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : 'Gerar rascunho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Dialog S-2200 (Admissão do Trabalhador)
// ---------------------------------------------------------------------------
const S2200Dialog = ({ open, onOpenChange, companyId, onCreated }) => {
  const [form, setForm] = useState({
    cpf_trab: '',
    nm_trab: '',
    sexo: 'M',
    raca_cor: '1',
    est_civ: '1',
    grau_instr: '07',
    dt_nascto: '',
    matricula: '',
    dt_admissao: new Date().toISOString().slice(0, 10),
    nm_cargo: 'ANALISTA',
    cbo_cargo: '252210',
    vr_sal_fx: '3000.00',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        ...form,
        cpf_trab: form.cpf_trab.replace(/\D/g, ''),
        cbo_cargo: form.cbo_cargo.replace(/\D/g, ''),
        ambiente: '2',
      };
      const r = await axios.post(
        `${API}/api/esocial/s2200/gerar?company_id=${companyId}`, body, auth(),
      );
      toast.success('S-2200 gerado em rascunho');
      onCreated(r.data.evento_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar S-2200');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>S-2200 — Admissão do Trabalhador</DialogTitle>
          <DialogDescription>
            Cadastra um vínculo CLT no eSocial. Pré-requisito: S-1000 + S-1005 + S-1010 + S-1020.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>CPF *</Label>
              <Input value={form.cpf_trab}
                     onChange={(e) => setForm({ ...form, cpf_trab: e.target.value })}
                     placeholder="00000000000" data-testid="s2200-cpf" />
            </div>
            <div>
              <Label>Nome *</Label>
              <Input value={form.nm_trab}
                     onChange={(e) => setForm({ ...form, nm_trab: e.target.value.toUpperCase() })}
                     data-testid="s2200-nome" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Data nascimento *</Label>
              <Input type="date" value={form.dt_nascto}
                     onChange={(e) => setForm({ ...form, dt_nascto: e.target.value })} />
            </div>
            <div>
              <Label>Sexo</Label>
              <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">M — Masculino</SelectItem>
                  <SelectItem value="F">F — Feminino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Est. Civil</Label>
              <Select value={form.est_civ} onValueChange={(v) => setForm({ ...form, est_civ: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Solteiro</SelectItem>
                  <SelectItem value="2">2 — Casado</SelectItem>
                  <SelectItem value="3">3 — Divorciado</SelectItem>
                  <SelectItem value="4">4 — Separado</SelectItem>
                  <SelectItem value="5">5 — Viúvo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Matrícula *</Label>
              <Input value={form.matricula}
                     onChange={(e) => setForm({ ...form, matricula: e.target.value })} />
            </div>
            <div>
              <Label>Data admissão *</Label>
              <Input type="date" value={form.dt_admissao}
                     onChange={(e) => setForm({ ...form, dt_admissao: e.target.value })} />
            </div>
            <div>
              <Label>Grau Instr.</Label>
              <Select value={form.grau_instr}
                      onValueChange={(v) => setForm({ ...form, grau_instr: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="01">01 — Analfabeto</SelectItem>
                  <SelectItem value="06">06 — Médio incompleto</SelectItem>
                  <SelectItem value="07">07 — Médio completo</SelectItem>
                  <SelectItem value="09">09 — Superior completo</SelectItem>
                  <SelectItem value="10">10 — Pós-graduação</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label>Cargo</Label>
              <Input value={form.nm_cargo}
                     onChange={(e) => setForm({ ...form, nm_cargo: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>CBO</Label>
              <Input value={form.cbo_cargo} maxLength={6}
                     onChange={(e) => setForm({ ...form, cbo_cargo: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Salário fixo mensal (R$)</Label>
            <Input value={form.vr_sal_fx}
                   onChange={(e) => setForm({ ...form, vr_sal_fx: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="s2200-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</> : 'Gerar rascunho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const EsocialCadastroSection = ({ companyId }) => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [open1000, setOpen1000] = useState(false);
  const [open1005, setOpen1005] = useState(false);
  const [open1010, setOpen1010] = useState(false);
  const [open1020, setOpen1020] = useState(false);
  const [open1070, setOpen1070] = useState(false);
  const [open2200, setOpen2200] = useState(false);
  // Maya analysis dialog
  const [mayaOpen, setMayaOpen] = useState(false);
  const [mayaEventoId, setMayaEventoId] = useState(null);
  const openMaya = (evId) => { setMayaEventoId(evId); setMayaOpen(true); };
  // Maya Lint pré-transmissão
  const [lintOpen, setLintOpen] = useState(false);
  const [lintEventoId, setLintEventoId] = useState(null);
  const openLint = (evId) => { setLintEventoId(evId); setLintOpen(true); };

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/esocial/cadastro/${companyId}`, auth());
      setEventos(r.data || []);
    } catch {
      toast.error('Erro ao listar eventos de cadastro');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const onCreated = () => load();

  const bytype = (tipo) => eventos.filter((e) => e.tipo === tipo);

  if (!companyId) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4 text-sm text-slate-600 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
          <span>Salve a empresa primeiro para enviar os eventos de cadastro inicial.</span>
        </CardContent>
      </Card>
    );
  }

  const renderTipoBloco = (tipo, label, icon, openSetter, descricao) => {
    const Icon = icon;
    const lista = bytype(tipo);
    return (
      <div className="space-y-2" data-testid={`esocial-cadastro-${tipo}-block`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-600" />
            <h4 className="font-semibold text-sm text-slate-800">{tipo} — {label}</h4>
          </div>
          <Button size="sm" variant="outline" onClick={() => openSetter(true)}
                  data-testid={`esocial-cadastro-${tipo}-new-btn`}>
            <Send className="h-4 w-4 mr-1" /> Novo
          </Button>
        </div>
        <p className="text-xs text-slate-500">{descricao}</p>
        {lista.length === 0 ? (
          <div className="border border-dashed rounded-md p-3 text-center text-xs text-slate-500">
            Nenhum {tipo} enviado para esta empresa.
          </div>
        ) : (
          <div className="space-y-1.5">
            {lista.map((ev) => (
              <div key={ev.id}
                   className="flex items-center justify-between border rounded-md px-3 py-2 text-xs"
                   data-testid={`esocial-cadastro-${tipo}-card`}>
                <div className="flex-1 min-w-0">
                  <code className="font-mono text-slate-700">{ev.id_esocial?.slice(0, 30)}…</code>
                  <div className="text-slate-500 mt-0.5">
                    {ev.criado_em && new Date(ev.criado_em).toLocaleString('pt-BR')}
                    {ev.numero_recibo && ` · recibo ${ev.numero_recibo}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={ev.status} />
                  {ev.status === 'rejeitado' && (
                    <Button size="sm" variant="outline"
                            onClick={() => openMaya(ev.id)}
                            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-1.5"
                            data-testid={`esocial-${tipo}-maya-btn`}>
                      <MayaAvatar variant="alert" size="xs" />
                      <span>Maya</span>
                    </Button>
                  )}
                  {['rascunho', 'rejeitado'].includes(ev.status) && (
                    <>
                      <Button size="sm" variant="outline"
                              onClick={() => openLint(ev.id)}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5"
                              data-testid={`esocial-${tipo}-lint-btn`}>
                        <MayaAvatar variant="thinking" size="xs" />
                        <span>Validar</span>
                      </Button>
                      <Button size="sm" variant="default" disabled={busy === ev.id}
                              onClick={() => flow(ev.id, setBusy, load)}
                              data-testid={`esocial-cadastro-${tipo}-send-btn`}>
                        {busy === ev.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><Send className="h-3 w-3 mr-1" />Assinar+Enviar</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="esocial-cadastro-section">
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {renderTipoBloco('S-1000', 'Informações do Empregador', Building2, setOpen1000,
            'Evento de cadastro inicial. Obrigatório antes de qualquer outro evento.')}
          {renderTipoBloco('S-1005', 'Tabela de Estabelecimentos', FileText, setOpen1005,
            'Define o CNAE preponderante e dados do estabelecimento.')}
          {renderTipoBloco('S-1020', 'Tabela de Lotações Tributárias', MapPin, setOpen1020,
            'Define a lotação (Matriz/Filial) com FPAS e código de terceiros.')}
          {renderTipoBloco('S-1010', 'Tabela de Rubricas', BookOpenCheck, setOpen1010,
            'Cadastra cada rubrica usada na folha de pagamento.')}
          {renderTipoBloco('S-1070', 'Processos Administrativos/Judiciais', AlertTriangle, setOpen1070,
            'Cadastra processo que suspende exigibilidade tributária ou FGTS (opcional).')}
          {renderTipoBloco('S-2200', 'Admissão do Trabalhador', UserPlus, setOpen2200,
            'Cadastra um vínculo CLT no eSocial — pré-requisito para S-1200/S-1210.')}
        </>
      )}

      <Card className="bg-amber-50/50 border-amber-200">
        <CardContent className="p-3 flex gap-3 text-xs">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <b>Ordem recomendada:</b> S-1000 → S-1005 → S-1010 (uma vez para cada rubrica).
            Esses 3 eventos habilitam a empresa para todos os outros (S-1200/S-1210/S-2200/etc.).
            Use ambiente <code className="bg-amber-100 px-1">2</code> (Homologação)
            para testes antes de transmitir em produção.
          </div>
        </CardContent>
      </Card>

      <S1000Dialog open={open1000} onOpenChange={setOpen1000}
                   companyId={companyId} onCreated={onCreated} />
      <S1005Dialog open={open1005} onOpenChange={setOpen1005}
                   companyId={companyId} onCreated={onCreated} />
      <S1020Dialog open={open1020} onOpenChange={setOpen1020}
                   companyId={companyId} onCreated={onCreated} />
      <S1010Dialog open={open1010} onOpenChange={setOpen1010}
                   companyId={companyId} onCreated={onCreated} />
      <S1070Dialog open={open1070} onOpenChange={setOpen1070}
                   companyId={companyId} onCreated={onCreated} />
      <S2200Dialog open={open2200} onOpenChange={setOpen2200}
                   companyId={companyId} onCreated={onCreated} />
      <MayaRejectionAnalysisDialog
        open={mayaOpen}
        onOpenChange={setMayaOpen}
        eventoId={mayaEventoId}
        onRetransmit={async (evId) => {
          await flow(evId, setBusy, load);
          setMayaOpen(false);
        }}
      />
      <MayaLintDialog
        open={lintOpen}
        onOpenChange={setLintOpen}
        eventoId={lintEventoId}
        onConfirmTransmit={async (evId) => {
          setLintOpen(false);
          await flow(evId, setBusy, load);
        }}
      />
    </div>
  );
};

export default EsocialCadastroSection;
